import { useEffect, useMemo, useState } from "react";
import { Minus, Plus, ShoppingBag, Trash2, Truck } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { FREE_SHIPPING_THRESHOLD, SHIPPING_FEE, getSettings, inr } from "@/lib/api";
import { CheckoutDialog } from "@/components/CheckoutDialog";

const CartDrawer = ({ open, onOpenChange }) => {
  const { items, setQty, remove, subtotal } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [threshold, setThreshold] = useState(FREE_SHIPPING_THRESHOLD);
  const [fee, setFee] = useState(SHIPPING_FEE);

  useEffect(() => {
    const openCheckout = () => {
      onOpenChange(true);
      setCheckoutOpen(true);
    };
    window.addEventListener("classina:buy-now", openCheckout);
    return () => window.removeEventListener("classina:buy-now", openCheckout);
  }, [onOpenChange]);

  useEffect(() => {
    getSettings()
      .then((s) => {
        setThreshold(s.free_shipping_above);
        setFee(s.shipping_fee);
      })
      .catch(() => {});
  }, []);

  const shipping = subtotal === 0 || subtotal >= threshold ? 0 : fee;
  const progress = Math.min(100, (subtotal / threshold) * 100);

  const groups = useMemo(() => {
    const map = {};
    for (const i of items) {
      if (!map[i.slug]) map[i.slug] = { slug: i.slug, name: i.name, min: i.min_qty || 1, total: 0 };
      map[i.slug].total += i.qty * (i.pieces || 1);
    }
    return map;
  }, [items]);
  const unmet = Object.values(groups).filter((g) => g.total < g.min);
  const canCheckout = items.length > 0 && unmet.length === 0;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent data-testid="cart-drawer" side="right" className="bg-background w-full sm:max-w-md flex flex-col p-0">
          <SheetHeader className="p-6 border-b border-border/60">
            <SheetTitle className="font-serif text-2xl flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" /> Your cart
            </SheetTitle>
          </SheetHeader>

          {items.length === 0 ? (
            <div data-testid="cart-empty" className="flex-1 flex flex-col items-center justify-center gap-4 p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-muted-foreground" />
              </div>
              <p className="font-serif text-xl">Your cart is empty</p>
              <p className="text-sm text-muted-foreground">Beautiful paper awaits. Start with our Eco Series.</p>
              <button
                data-testid="cart-continue-shopping"
                onClick={() => onOpenChange(false)}
                className="mt-2 rounded-full bg-primary text-primary-foreground px-8 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform"
              >
                Browse products
              </button>
            </div>
          ) : (
            <>
              <div className="px-6 pt-5">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                  <Truck className="w-3.5 h-3.5 text-primary" />
                  {shipping === 0 ? (
                    <span data-testid="free-shipping-unlocked" className="font-semibold text-primary">You've unlocked free shipping</span>
                  ) : (
                    <span data-testid="free-shipping-progress">Add {inr(threshold - subtotal)} more for free shipping</span>
                  )}
                </div>
                <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                {items.map((item) => (
                  <div key={item.id} data-testid={`cart-item-${item.id.replaceAll("|", "--")}`} className="flex gap-4">
                    <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover border border-border/60" />
                    <div className="flex-1 min-w-0">
                      <p className="font-serif font-medium text-sm leading-snug truncate">{item.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {item.variant && <span className="text-primary font-semibold">{item.variant} · </span>}
                        {item.pack && <span className="text-accent font-semibold">{item.pack} · </span>}
                        {inr(item.price)} {item.pack ? "per pack" : "each"}
                      </p>
                      {(item.min_qty || 1) > 1 && groups[item.slug] && (
                        <p
                          data-testid={`set-progress-${item.slug}`}
                          className={`text-[11px] font-bold mt-1 ${groups[item.slug].total >= item.min_qty ? "text-primary" : "text-accent"}`}
                        >
                          {groups[item.slug].total >= item.min_qty
                            ? `Set of ${item.min_qty} complete ✓ · ${groups[item.slug].total} pcs counted in cart`
                            : `Set of ${item.min_qty} · ${groups[item.slug].total} of ${item.min_qty} pcs in cart so far`}
                        </p>
                      )}
                      <div className="mt-2 flex items-center justify-between">
                        <div className="flex items-center border border-border rounded-full">
                          <button
                            data-testid={`cart-minus-${item.id.replaceAll("|", "--")}`}
                            onClick={() => setQty(item.id, item.qty - 1)}
                            className="p-1.5 hover:text-primary transition-colors"
                            aria-label="Decrease"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span data-testid={`cart-qty-${item.id.replaceAll("|", "--")}`} className="w-7 text-center text-xs font-bold">{item.qty}</span>
                          <button
                            data-testid={`cart-plus-${item.id.replaceAll("|", "--")}`}
                            onClick={() => setQty(item.id, item.qty + 1)}
                            className="p-1.5 hover:text-primary transition-colors"
                            aria-label="Increase"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <button
                          data-testid={`cart-remove-${item.id.replaceAll("|", "--")}`}
                          onClick={() => remove(item.id)}
                          className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                          aria-label={`Remove ${item.name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm font-bold whitespace-nowrap">{inr(item.price * item.qty)}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-border/60 p-6 space-y-2 bg-secondary/30">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span data-testid="cart-subtotal" className="font-semibold">{inr(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span data-testid="cart-shipping" className="font-semibold">{shipping === 0 ? "Free" : inr(shipping)}</span>
                </div>
                <div className="flex justify-between font-serif text-xl font-semibold pt-2 border-t border-border/60">
                  <span>Total</span>
                  <span data-testid="cart-total">{inr(subtotal + shipping)}</span>
                </div>
                {unmet.length > 0 && (
                  <p data-testid="set-min-warning" className="text-[11px] text-accent font-semibold text-center pt-1">
                    {unmet.map((g) => `${g.name}: add ${g.min - g.total} more pc${g.min - g.total > 1 ? "s" : ""} to complete the set`).join(" · ")}
                  </p>
                )}
                <button
                  data-testid="checkout-button"
                  onClick={() => setCheckoutOpen(true)}
                  disabled={!canCheckout}
                  className="w-full mt-3 rounded-full bg-primary text-primary-foreground py-3.5 text-sm font-bold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                >
                  Proceed to checkout
                </button>
                <p className="text-[11px] text-center text-muted-foreground">Pay on confirmation · COD available across India</p>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <CheckoutDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        onOrderPlaced={() => onOpenChange(false)}
      />
    </>
  );
};

export default CartDrawer;
